/**
 * Final step: what actually happened, including what did not come in.
 */

import { CheckCircle2, XCircle } from 'lucide-react'
import { ScrollArea } from '@/components/ui/scroll-area'
import { UnmatchedItemsList } from './UnmatchedItemsList'
import type { ExecuteResult } from '@/lib/hangarmax-import'

interface ImportResultReportProps {
  result: ExecuteResult
}

export function ImportResultReport({ result }: ImportResultReportProps) {
  const { counts, errors, unmatched } = result
  const succeeded = errors.length === 0

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        {succeeded ? (
          <CheckCircle2 className="h-5 w-5 text-emerald-600" />
        ) : (
          <XCircle className="h-5 w-5 text-destructive" />
        )}
        <span className="text-sm font-medium">
          {succeeded
            ? 'Import complete'
            : `Import finished with ${errors.length} error${errors.length === 1 ? '' : 's'}`}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        <Stat label="Created" value={counts.created} />
        <Stat label="Updated" value={counts.updated} />
        <Stat label="Unchanged" value={counts.unchanged} />
        <Stat label="Not imported" value={counts.unmatched} />
        <Stat label="Removed in source" value={counts.sourceDeleted} />
      </div>

      {errors.length > 0 && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5">
          <div className="px-3 py-2 text-sm font-medium text-destructive">
            Failed objects — re-running the import retries them
          </div>
          <ScrollArea className="max-h-40">
            <ul className="space-y-1 px-3 pb-3">
              {errors.map((error, i) => (
                <li key={`${error.label}-${i}`} className="text-xs">
                  <span className="font-medium">{error.label}</span>
                  <span className="text-muted-foreground"> — {error.message}</span>
                </li>
              ))}
            </ul>
          </ScrollArea>
        </div>
      )}

      <UnmatchedItemsList items={unmatched} />
    </div>
  )
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border px-3 py-2">
      <div className="text-lg font-semibold tabular-nums">{value}</div>
      <div className="text-[11px] text-muted-foreground">{label}</div>
    </div>
  )
}
